// Hợp đồng dữ liệu "Phân tích Creator" — CHÉP từ backend (nguồn sự thật là code, không phải văn bản đặc tả):
//   hp-media-server-staging/src/analytics/analytics.types.ts      (CreatorKind, Trend, PaceStatus, Insight, CreatorFacts)
//   hp-media-server-staging/src/analytics/analytics-overview.ts   (OverviewResponse — lệch đặc tả §7: bộ lọc Loại là
//                                                                  `scope.creatorKind`, có `seriesAvailable`, KPI có `prevN`)
//   hp-media-server-staging/src/analytics/analytics-read.service.ts (AccessResponse, CreatorRow, CreatorDetailResponse)
//   hp-media-server-staging/src/analytics/analytics.errors.ts     (ANALYTICS_ERROR_TEXT)
// Sửa bên đó thì sửa cả ở đây. Trường đợt 1b/1c (cột sau của file TikTok) ở contract-more.ts.

import type { CompareMode, CreatorFactsMore, OverviewMore } from './contract-more';

export type {
  CohortKey,
  CompareMetric,
  CompareMode,
  CompareValue,
  CountKc,
  CreatorCompare,
  CreatorEfficiency,
  CreatorMore,
  CreatorRegion,
  EfficiencyTopRow,
  FactsSource,
  OverseasTopRow,
  OverviewCompare,
  OverviewKpi,
  OverviewRegion,
  TiktokFive,
  TiktokMonthFacts,
} from './contract-more';

export type CreatorKind = 'ONLINE' | 'STUDIO' | 'NPC' | 'DANCE';
export type Trend = 'UP' | 'FLAT' | 'DOWN' | 'NA';
export type PaceStatus = 'AHEAD' | 'ON_TRACK' | 'BEHIND' | 'UNREACHABLE' | 'DONE';
export type ScopeKind = 'ALL' | 'GROUPS' | 'SELF' | 'NONE';
export type ScopeReason = 'ANALYTICS_NO_ACCESS' | 'ANALYTICS_NO_SCOPE' | 'ANALYTICS_NO_UID';
export type CreatorSort =
  | 'diamonds'
  | 'delta7'
  | 'needDays'
  | 'liveDays'
  | 'liveSeconds'
  | 'attention';

export interface Insight {
  code: string;
  type: 'goal' | 'streak' | 'trend' | 'pace' | 'compare' | 'anomaly' | 'mix' | 'info';
  severity: 'danger' | 'warn' | 'info';
  title: string;
  text: string;
  metric?: string;
  value?: number | null;
}

export interface CreatorFacts extends CreatorFactsMore {
  uid: string;
  username: string | null;
  groupName: string | null;
  groupId: number | null;
  userId: number | null;
  kind: CreatorKind;
  month: string;
  asOfDay: string;
  daysInMonth: number;
  daysElapsed: number;
  daysRemaining: number;
  totals: {
    diamonds: number;
    liveSeconds: number;
    liveDays: number;
    newFollowers: number;
    liveSessions: number;
    pkSessions: number;
    pkDiamonds: number;
  };
  series: {
    days: string[];
    diamonds: number[];
    liveSeconds: number[];
    liveDays: number[];
    newFollowers: number[];
  };
  delta7: { now: number; prev: number; pct: number | null } | null;
  trend: Trend;
  prevMonth: {
    month: string;
    n: number;
    diamonds: number;
    liveSeconds: number;
    liveDays: number;
    pct: { diamonds: number | null; liveSeconds: number | null; liveDays: number | null };
  } | null;
  goal: { level: number; kc: number; days: number; hours: number } | null;
  pace: {
    expectedKc: number;
    pacePct: number | null;
    rate: number;
    projectedKc: number;
    needKc: number;
    needDays: number;
    needSeconds: number;
    requiredKcPerDay: number | null;
    requiredHoursPerLiveDay: number | null;
    daysFeasible: boolean;
    bottleneck?: 'kc' | 'days' | 'hours' | null;
    status: PaceStatus;
  } | null;
  streak: { liveStreak: number; restStreak: number; lastLiveDay: string | null };
  anomalies: { day: string; diamonds: number; z: number }[];
  mix: {
    pkDiamonds: number;
    otherDiamonds: number;
    overseasDiamonds: number | null;
    multiGuestDiamonds: number | null;
    fanClubDiamonds: number | null;
  };
  extras: {
    daysSinceJoin: number | null;
    totalFans: number | null;
    newFans: number | null;
    overseasRatio: number | null;
    isNewLiveCreator: boolean | null;
    status: string | null;
  };
  rank: { diamonds: number | null; total: number };
}

export interface AnalyticsGroup {
  id: number;
  name: string;
  kind: 'STUDIO' | 'AGENCY';
}

export interface AccessResponse {
  canView: boolean;
  scope: ScopeKind;
  reason?: ScopeReason;
  groups: AnalyticsGroup[];
  /** Tháng có file ngày, mới → cũ, tối đa 12. */
  months: string[];
  isAdmin: boolean;
}

export interface Coverage {
  asOfDay: string | null;
  daysInMonth: number;
  daysElapsed: number;
  daysRemaining: number;
  missingDays: string[];
  /** 'MONTH_FILE' = tháng tính theo file tháng TikTok (không có số theo ngày). */
  source?: 'DAY' | 'MONTH_FILE';
}

export interface KpiValue {
  value: number;
  prev: number | null;
  /** % đã ×100, 1 số lẻ (12.3 = +12,3%). */
  pct: number | null;
  /** Số ngày đầu tháng đã so với tháng trước; null khi không so được. */
  prevN: number | null;
  trend: Trend;
  series: number[];
  /** Nguồn của prev / pct (đợt 1b); null = không so được. */
  compareMode?: CompareMode | null;
}

export interface CreatorRef {
  uid: string;
  username: string | null;
  groupName: string | null;
}

export interface Mover extends CreatorRef {
  diamonds: number;
  delta7Pct: number;
}

export interface AtRiskRow extends CreatorRef {
  level: number | null;
  needDays: number;
  daysRemaining: number;
  needKc: number;
}

export interface RankingRow extends CreatorRef {
  kind: CreatorKind;
  diamonds: number;
  liveHours: number;
  liveDays: number;
  trend: Trend;
  delta7Pct: number | null;
  paceStatus: PaceStatus | null;
  needDays: number | null;
  dangerCount: number;
  warnCount: number;
}

export interface HeatCell {
  day: string;
  diamonds: number | null;
  activeCreators: number | null;
}

export interface OverviewResponse extends OverviewMore {
  month: string;
  empty: boolean;
  /** false → mọi chuỗi theo ngày là [] và heatmap toàn null (tháng cũ chỉ còn số tổng). */
  seriesAvailable: boolean;
  coverage: Coverage;
  scope: {
    kind: ScopeKind;
    creators: number;
    groupId: number | null;
    creatorKind: CreatorKind | null;
  };
  kpis: {
    diamonds: KpiValue;
    liveHours: KpiValue;
    liveDays: KpiValue;
    activeCreators: {
      value: number;
      total: number;
      pct: null;
      prevN: null;
      trend: 'NA';
      series: number[];
    };
  };
  insights: Insight[];
  series: {
    days: string[];
    diamonds: number[];
    ma7: (number | null)[];
    liveHours: number[];
    activeCreators: number[];
    anomalies: { day: string; z: number }[];
  };
  topUp: Mover[];
  topDown: Mover[];
  atRisk: AtRiskRow[];
  attention: { danger: number; warn: number };
  mix: {
    pk: number;
    other: number;
    overseas: number | null;
    multiGuest: number | null;
    fanClub: number | null;
  };
  byGroup: {
    groupId: number | null;
    name: string;
    diamonds: number;
    creators: number;
    share: number | null;
  }[];
  byKind: { kind: CreatorKind; diamonds: number; creators: number }[];
  heatmap: HeatCell[];
  ranking: RankingRow[];
}

export interface CreatorRow extends CreatorRef {
  groupId: number | null;
  kind: CreatorKind;
  diamonds: number;
  liveHours: number;
  liveDays: number;
  newFollowers: number;
  trend: Trend;
  delta7Pct: number | null;
  paceStatus: PaceStatus | null;
  needDays: number | null;
  dangerCount: number;
  warnCount: number;
  topInsight: Insight | null;
}

export interface CreatorsResponse {
  rows: CreatorRow[];
  total: number;
  page: number;
  limit: number;
}

export interface MonthTotals {
  month: string;
  diamonds: number;
  liveSeconds: number;
  liveDays: number;
}

export interface CreatorDetailResponse {
  /** null khi UID không có dòng ảnh chụp của tháng. */
  facts: CreatorFacts | null;
  insights: Insight[];
  /** ≤ 3 tháng liền trước, cũ → mới. */
  history: MonthTotals[];
}

export interface RecomputeResult {
  month: string;
  uids: number;
  ms: number;
}

/** Lỗi nghiệp vụ backend (409/400): AxiosUtils ném nguyên `response.data`. */
export interface AnalyticsApiError {
  statusCode?: number;
  code?: string;
  message?: string;
  networkError?: boolean;
}

export const ANALYTICS_ERROR_TEXT: Record<string, string> = {
  ANALYTICS_NO_ACCESS: 'Bạn chưa được cấp quyền xem Phân tích Creator.',
  ANALYTICS_NO_SCOPE: 'Bạn chưa được gán nhóm hoặc Studio nào để xem phân tích.',
  ANALYTICS_NO_UID: 'Tài khoản của bạn chưa gắn UID TikTok nên chưa có dữ liệu phân tích.',
  ANALYTICS_SCOPE: 'Creator hoặc nhóm này nằm ngoài phạm vi bạn được xem.',
  ANALYTICS_MONTH_INVALID: 'Tháng không hợp lệ (YYYY-MM).',
  ANALYTICS_ADMIN_ONLY: 'Việc này chỉ Admin làm được.',
  ANALYTICS_UNAVAILABLE: 'Dữ liệu phân tích chưa sẵn sàng (máy chủ chưa tạo bảng). Hãy báo Admin.',
};
